<template>
    <dtg-layout-without-sidebar :class="b()" :loading="loading">
        <dtg-title view="1">
            Liste des dossiers
        </dtg-title>

        <dtg-grid>
            <dtg-row-col v-if="shownFilters">
                <div :class="b('filters')">
                    <dtg-grid>
                        <dtg-row>
                            <dtg-col
                                size-xs="12"
                                size-sm="6"
                                size-lg
                            >
                                <dtg-field label="Nom dossier">
                                    <dtg-input v-model="queries.name" name="name" />
                                </dtg-field>
                            </dtg-col>
                            <dtg-col
                                size-xs="12"
                                size-sm="6"
                                size-lg="2.4"
                            >
                                <dtg-field label="Date de visite">
                                    <dtg-input-date v-model="queries.visitDate" name="visitDate" />
                                </dtg-field>
                            </dtg-col>
                            <dtg-col
                                size-xs="12"
                                size-sm="6"
                                size-lg="2.4"
                            >
                                <dtg-field label="Date de rendu attendu">
                                    <dtg-input-date v-model="queries.endDate" name="endDate" />
                                </dtg-field>
                            </dtg-col>
                            <dtg-col
                                size-xs="12"
                                size-sm="6"
                                size-lg="2.9"
                            >
                                <dtg-field label="Statut">
                                    <dtg-select
                                        v-model="queries.status"

                                        :options="dtgStatuses"

                                        name="status"

                                        tag
                                    />
                                </dtg-field>
                            </dtg-col>
                            <dtg-col v-if="shownMineFilter" size-lg="1.8">
                                <dtg-field label="Mes affectations">
                                    <dtg-checkbox
                                        v-model="queries.mine"

                                        name="mine"
                                    >
                                        Afficher
                                    </dtg-checkbox>
                                </dtg-field>
                            </dtg-col>
                            <dtg-col :class="b('col-reset')">
                                <dtg-action
                                    :class="b('reset-button')"

                                    :disabled="!filtersApplied"

                                    @click="onResetFiltersClick()"
                                >
                                    <dtg-icon :class="b('reset-button-icon')" name="closeCircle" />
                                    Réinitialiser les&nbsp;filtres
                                </dtg-action>
                            </dtg-col>
                        </dtg-row>
                    </dtg-grid>
                </div>
            </dtg-row-col>

            <dtg-row v-if="shownFilters" :class="b('sort-row')">
                <dtg-col :class="b('col-sort')">
                    <dtg-list-sort-controls
                        v-model:sortKey="sortKey"
                        v-model:sortDirection="sortDirection"

                        @sort-click="setSortKey($event)"
                    />
                </dtg-col>
            </dtg-row>

            <dtg-row-col>
                <div :class="b('list-wrapper')">
                    <dtg-transition-group-stack>
                        <div
                            v-for="item in list"
                            :key="item.id"
                        >
                            <dtg-list-item
                                :class="b('item')"

                                :dtg-id="item.id"
                            />
                        </div>
                    </dtg-transition-group-stack>
                </div>
            </dtg-row-col>

            <dtg-row-col v-if="noDtgs">
                <dtg-title view="2">
                    Aucun DTG
                    <dtg-icon-action
                        v-if="refreshShown"

                        name="reload"

                        @click="onRefresh()"
                    />
                </dtg-title>
            </dtg-row-col>
        </dtg-grid>
    </dtg-layout-without-sidebar>
</template>

<script lang="ts">
import partition from 'lodash/partition';
import { computed, defineComponent, onActivated, onMounted, reactive, watch } from 'vue';

import {
    DtgAction,
    DtgCheckbox,
    DtgCol,
    DtgField,
    DtgGrid,
    DtgIcon,
    DtgIconAction,
    DtgInput,
    DtgInputDate,
    DtgRow,
    DtgRowCol,
    DtgSelect,
    DtgTitle,
    DtgTransitionGroupStack,
    getName,
    useBem,
} from 'dtg-components';
import { DtgData, DtgStatus } from 'dtg-firebase/modules/dtg';
import { useFilters } from 'dtg-shared/composables/use-filters';
import { useSortableList } from 'dtg-shared/composables/use-sortable-list';
import { Id, Option } from 'dtg-shared/types/data';
import { isSame } from 'dtg-shared/utils/date';
import { normalizeString } from 'dtg-shared/utils/string';
import { truthy } from 'dtg-shared/utils/truthy';

import { DtgLayoutWithoutSidebar } from '@/components/layouts/layout-without-sidebar';

import { useAuth } from '@/composables/use-auth';
import { useDtgListData } from '@/composables/use-dtg-list-data';
import { useDtgPrefetch } from '@/composables/use-dtg-prefetch';
import { useNetworkStatus } from '@/composables/use-network-status';

import { useStoreCollection } from '@/store';

import { DtgListItem } from './elems/list-item';
import { DtgListSortControls } from './elems/list-sort-controls';

import styles from './style.module.scss';

export default defineComponent({
    name: getName(styles),

    components: {
        DtgListItem,
        DtgGrid,
        DtgRow,
        DtgTitle,
        DtgCol,
        DtgInput,
        DtgInputDate,
        DtgSelect,
        DtgCheckbox,
        DtgField,
        DtgRowCol,
        DtgLayoutWithoutSidebar,
        DtgTransitionGroupStack,
        DtgListSortControls,
        DtgAction,
        DtgIcon,
        DtgIconAction,
    },

    setup() {
        const {
            getters: { getItems: getDtgItems, getDtgStatuses },
        } = useStoreCollection('dtg');

        const { online } = useNetworkStatus();

        const { user } = useAuth();

        const dtgs = computed(() => {
            const sortedDtgs = getDtgItems().filter((dtg) => {
                const { packId, status } = dtg.data;

                return !packId || status !== DtgStatus.planning;
            });

            const [validDtgs, invalidDtgs] = partition(sortedDtgs, ({ valid }) => valid);

            return [...invalidDtgs, ...validDtgs];
        });

        const dtgItems = computed<Array<DtgData & Id>>(() => {
            return dtgs.value.map(({ id, data }) => ({ id, ...data }));
        });

        const dtgStatuses = computed<Array<Option<DtgStatus>>>(() => {
            const availableStatuses = new Set(dtgs.value.map(({ data }) => data.status));

            return getDtgStatuses().filter(({ id }) => availableStatuses.has(id));
        });

        const shownFilters = computed(() => dtgItems.value.length > 0);

        const shownMineFilter = computed(() => {
            const userId = user.value?.uid;

            return (
                userId &&
                dtgItems.value.some((dtg) => dtg.technicianId === userId || dtg.validatorId === userId) &&
                dtgItems.value.some((dtg) => dtg.technicianId !== userId && dtg.validatorId !== userId)
            );
        });

        const { filteredItems, filtersApplied, queries, resetQueries } = useFilters({
            items: dtgItems,
            queries: reactive({
                name: '',
                visitDate: null as string | null,
                endDate: null as string | null,
                status: null as DtgStatus | null,
                mine: false,
            }),
            filters: {
                name: (value, query) => normalizeString(value).includes(normalizeString(query)),
                visitDate: (value, query) => !value || (truthy(query) && isSame(value, query)),
                endDate: (value, query) => !value || (truthy(query) && isSame(value, query)),
                mine: (item) => {
                    if (!user.value || !shownMineFilter.value) {
                        return true;
                    }

                    const { uid } = user.value;

                    return truthy(item.technicianId) && uid === item.technicianId;
                },
            },
        });

        const { list, sortKey, sortDirection, setSortKey } = useSortableList<DtgData>(
            computed(() => ({
                fields: [{ key: 'name' }, { key: 'visitDate' }, { key: 'endDate' }, { key: 'status' }],
                list: filteredItems.value,
                defaultSortKey: 'endDate',
            })),
        );

        const noDtgs = computed(() => filteredItems.value.length === 0);
        const refreshShown = computed(() => dtgItems.value.length === 0);

        const { loading, fetchDtgs } = useDtgListData();
        const { restartPrefetch } = useDtgPrefetch();

        onMounted(() => {
            void fetchDtgsInternal();
        });

        onActivated(() => {
            void fetchDtgsInternal();
        });

        watch(shownFilters, (value) => {
            if (!value) {
                resetQueries();
            }
        });

        async function fetchDtgsInternal(refresh?: boolean): Promise<void> {
            await fetchDtgs(refresh);

            restartPrefetch();
        }

        async function refresh(): Promise<void> {
            if (online.value) {
                await fetchDtgsInternal(true);
            }
        }

        async function onRefresh({ complete }: { complete?: () => Promise<void> } = {}): Promise<void> {
            void complete?.();

            await refresh();

            resetQueries();
        }

        function onResetFiltersClick(): void {
            resetQueries();
        }

        return {
            b: useBem(styles),

            noDtgs,
            refreshShown,
            loading,
            list,
            filtersApplied,
            queries,
            shownMineFilter,
            shownFilters,
            dtgStatuses,
            sortKey,
            sortDirection,

            setSortKey,
            onRefresh,
            onResetFiltersClick,
        };
    },
});
</script>
